import { Link } from "react-router-dom";
import { Target, Eye, Users, ArrowRight, Heart, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Section, Eyebrow, H1, H2, Lead, FadeIn } from "../components/Primitives";
import { STATS } from "../data/content";

const team = [
  { name: "Nishant Kumar", role: "Co-Founder", initials: "NK" },
  { name: "Aditya Kumar", role: "Co-Founder", initials: "AK" },
];

const values = [
  { icon: Target, title: "Mission", body: "To digitize every manual register in India — across every shop, school, hospital and office." },
  { icon: Eye, title: "Vision", body: "A Paperless India by 2030 — where work moves at the speed of a smartphone." },
  { icon: Heart, title: "Values", body: "Built in India, for India. Simple. Honest. Bilingual. Affordable." },
];

export default function AboutPage() {
  return (
    <div data-testid="page-about">
      <Section className="pt-24">
        <FadeIn>
          <Eyebrow>About us</Eyebrow>
        </FadeIn>
        <FadeIn delay={0.05}>
          <H1 className="mt-6 max-w-3xl">We started Register to Digital because India still runs on paper.</H1>
        </FadeIn>
        <FadeIn delay={0.1}>
          <Lead className="mt-6">
            One afternoon in Meerut, our founder watched a clinic spend forty minutes finding a single patient record in a stack
            of OPD registers. That evening, the first prototype was born. Today, hundreds of teams across India use Register to
            Digital to retire their paper registers — quietly, affordably, and one photo at a time.
          </Lead>
        </FadeIn>
      </Section>

      {/* Mission/Vision/Values */}
      <Section testId="about-values" className="bg-zinc-50 border-y border-zinc-200">
        <div className="grid gap-5 md:grid-cols-3">
          {values.map((v, i) => (
            <FadeIn key={v.title} delay={i * 0.06}>
              <div className="h-full rounded-2xl border border-zinc-200 bg-white p-8">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-950 text-white">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-poppins text-xl font-semibold text-zinc-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{v.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* Stats */}
      <Section testId="about-stats">
        <div className="rounded-3xl border border-zinc-200 bg-white p-10 md:p-14">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-poppins text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">{s.value}</div>
                <div className="mt-2 text-xs uppercase tracking-wider text-zinc-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Team */}
      <Section testId="about-team">
        <div className="grid items-end gap-6 md:grid-cols-2">
          <div>
            <Eyebrow>The team</Eyebrow>
            <H2 className="mt-4">A small Meerut-based team obsessed with paperless work.</H2>
          </div>
          <Lead>Two founders, one mission — quietly retiring the paper register from every Indian workplace.</Lead>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {team.map((m) => (
            <div key={m.name} data-testid={`team-${m.name.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-2xl border border-zinc-200 bg-white p-8">
              <div className="grid h-16 w-16 place-items-center rounded-full bg-zinc-950 font-poppins text-xl font-semibold text-white">
                {m.initials}
              </div>
              <div className="mt-5 font-poppins text-lg font-semibold text-zinc-950">{m.name}</div>
              <div className="text-sm text-zinc-500">{m.role}</div>
              <div className="mt-1 text-xs text-zinc-400">RegisterToDigital</div>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section className="pb-32">
        <div className="rounded-3xl border border-zinc-200 bg-zinc-50 p-10 md:p-14 text-center">
          <Sparkles className="mx-auto h-6 w-6 text-zinc-900" />
          <h2 className="mt-3 font-poppins text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950">Help us build a Paperless India.</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-zinc-600">Join 500+ Indian businesses who&apos;ve already moved their registers to the cloud.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="about-cta-primary">
              <Button size="lg" className="rounded-full bg-zinc-950 text-white hover:bg-zinc-800">Start free trial <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
            </Link>
            <Link to="/features" data-testid="about-cta-secondary">
              <Button size="lg" variant="outline" className="rounded-full border-zinc-300">Explore features</Button>
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
